(() => {
  const API_BASE = 'https://shopi.gaia.design/api/Precios';
  const CASH_EXTRA_DISCOUNT = 0.1;
  const CACHE_TTL_MS = 5 * 60 * 1000;
  const MAX_DESCUENTOS = 20;
  const DISCOUNT_SLOTS = 4;
  const precioCache = new Map();

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function roundMoney(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return 0;
    return Math.round((number + Number.EPSILON) * 100) / 100;
  }

  function roundPercent(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return 0;
    return Math.round((number + Number.EPSILON) * 100) / 100;
  }

  function formatPercentLabel(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '';
    return String(Math.floor(number));
  }

  function formatMoney(amountInCents, moneyFormat) {
    if (amountInCents === null || amountInCents === undefined) return '';

    if (typeof formatPrice === 'function') {
      return formatPrice(amountInCents, moneyFormat);
    }

    if (
      typeof Shopify !== 'undefined' &&
      typeof Shopify.formatMoney === 'function'
    ) {
      return Shopify.formatMoney(Math.round(Number(amountInCents)), moneyFormat);
    }

    const value = Number(amountInCents) / 100;
    if (!Number.isFinite(value)) return '';

    return `$${value.toLocaleString('es-MX', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  /**
   * Normaliza 4 descuentos (array numérico u objetos con orden/porcentaje).
   * @param {Array<number|{orden?: number, porcentaje?: number}>|object|null} descuentos
   * @returns {[number, number, number, number]}
   */
  function normalizeDiscountPercents(descuentos) {
    const slots = Array(DISCOUNT_SLOTS).fill(0);

    if (Array.isArray(descuentos)) {
      const looksNumeric = descuentos.every(
        (item) => typeof item === 'number' || item == null || item === ''
      );

      if (looksNumeric) {
        for (let i = 0; i < DISCOUNT_SLOTS; i += 1) {
          const value = Number(descuentos[i]);
          slots[i] = Number.isFinite(value) && value > 0 ? value : 0;
        }
        return slots;
      }

      descuentos.slice(0, MAX_DESCUENTOS).forEach((item) => {
        const orden = Number(item?.orden);
        const porcentaje = Number(item?.porcentaje);
        if (
          Number.isFinite(orden) &&
          orden >= 1 &&
          orden <= DISCOUNT_SLOTS &&
          Number.isFinite(porcentaje) &&
          porcentaje > 0
        ) {
          slots[orden - 1] = porcentaje;
        }
      });
      return slots;
    }

    if (descuentos && typeof descuentos === 'object') {
      for (let i = 1; i <= DISCOUNT_SLOTS; i += 1) {
        const value = Number(descuentos[i] ?? descuentos[`d${i}`]);
        if (Number.isFinite(value) && value > 0) slots[i - 1] = value;
      }
    }

    return slots;
  }

  /**
   * Slot con datos útiles: fraccion > 0 y porcentaje > 0.
   * @param {{fraccion?: number, porcentaje?: number}|number|null|undefined} item
   * @returns {boolean}
   */
  function hasDiscountSlotData(item) {
    if (item == null || item === '') return false;

    if (typeof item === 'number') {
      return Number.isFinite(item) && item > 0;
    }

    if (typeof item !== 'object') {
      const value = Number(item);
      return Number.isFinite(value) && value > 0;
    }

    const fraccion = Number(item.fraccion);
    const porcentaje = Number(item.porcentaje);
    return (
      Number.isFinite(fraccion) &&
      fraccion > 0 &&
      Number.isFinite(porcentaje) &&
      porcentaje > 0
    );
  }

  /**
   * True cuando solo los descuentos 1 y 4 tienen data (2 y 3 vacíos).
   * En ese caso no se aplica la suma progresiva d1–d3.
   * @param {Array|object|null} descuentos
   * @param {[number, number, number, number]} slots
   * @returns {boolean}
   */
  function isOnlyDiscount1And4(descuentos, slots) {
    const [d1, d2, d3, d4] = slots;

    if (!(d1 > 0 && d4 > 0 && d2 === 0 && d3 === 0)) return false;

    if (!Array.isArray(descuentos)) return true;

    const looksNumeric = descuentos.every(
      (item) => typeof item === 'number' || item == null || item === ''
    );
    if (looksNumeric) return true;

    const byOrden = { 1: null, 2: null, 3: null, 4: null };
    descuentos.slice(0, MAX_DESCUENTOS).forEach((item) => {
      const orden = Number(item?.orden);
      if (orden >= 1 && orden <= DISCOUNT_SLOTS) {
        byOrden[orden] = item;
      }
    });

    return (
      hasDiscountSlotData(byOrden[1]) &&
      hasDiscountSlotData(byOrden[4]) &&
      !hasDiscountSlotData(byOrden[2]) &&
      !hasDiscountSlotData(byOrden[3])
    );
  }

  function compoundRemainingFactor(percents) {
    return percents.reduce((factor, percent) => {
      const safe = Math.min(Math.max(Number(percent) || 0, 0), 100);
      return factor * (1 - safe / 100);
    }, 1);
  }

  /**
   * Calcula descuentos en 2 bloques (d1–d3 y d4) para que la suma de % cuadre.
   * Si solo hay data en d1 y d4, usa esos % tal cual (sin fórmula progresiva).
   * @param {number} precioBase Precio original en unidades monetarias (no centavos)
   * @param {Array|object} descuentos 4 porcentajes o items API con orden/porcentaje
   * @returns {object|null}
   */
  function calcularDescuentosAgrupados(precioBase, descuentos) {
    const precioOriginal = roundMoney(precioBase);
    if (!Number.isFinite(precioOriginal) || precioOriginal <= 0) return null;

    const slots = normalizeDiscountPercents(descuentos);
    const [d1, d2, d3, d4] = slots;

    if (isOnlyDiscount1And4(descuentos, slots)) {
      const porcentajeBloque1 = roundPercent(d1);
      const porcentajeBloque2 = roundPercent(d4);
      const porcentajeTotalDescuento = roundPercent(
        porcentajeBloque1 + porcentajeBloque2
      );
      const montoDescuentoBloque1 = roundMoney(
        precioOriginal * (porcentajeBloque1 / 100)
      );
      const montoDescuentoBloque2 = roundMoney(
        precioOriginal * (porcentajeBloque2 / 100)
      );
      const montoAhorroTotal = roundMoney(
        montoDescuentoBloque1 + montoDescuentoBloque2
      );

      return {
        precioOriginal,
        descuentos: [d1, d2, d3, d4],
        modoDirecto: true,
        porcentajeBloque1,
        montoDescuentoBloque1,
        porcentajeBloque2,
        porcentajeD4Nominal: roundPercent(d4),
        montoDescuentoBloque2,
        porcentajeTotalDescuento,
        montoAhorroTotal,
        precioFinal: roundMoney(precioOriginal - montoAhorroTotal),
        precioTrasBloque1: roundMoney(precioOriginal - montoDescuentoBloque1),
      };
    }

    const factorBloque1 = compoundRemainingFactor([d1, d2, d3]);
    const descBloque1 = 1 - factorBloque1;
    const porcentajeBloque1 = roundPercent(descBloque1 * 100);
    const montoDescuentoBloque1 = roundMoney(precioOriginal * descBloque1);

    const factorTotal = factorBloque1 * (1 - Math.min(Math.max(d4, 0), 100) / 100);
    const descTotal = 1 - factorTotal;
    const porcentajeTotalDescuento = roundPercent(descTotal * 100);
    const porcentajeBloque2 = roundPercent(
      porcentajeTotalDescuento - porcentajeBloque1
    );
    const montoDescuentoBloque2 = roundMoney(precioOriginal * (porcentajeBloque2 / 100));
    const montoAhorroTotal = roundMoney(montoDescuentoBloque1 + montoDescuentoBloque2);
    const precioFinal = roundMoney(precioOriginal - montoAhorroTotal);

    return {
      precioOriginal,
      descuentos: [d1, d2, d3, d4],
      modoDirecto: false,
      porcentajeBloque1,
      montoDescuentoBloque1,
      porcentajeBloque2,
      porcentajeD4Nominal: roundPercent(d4),
      montoDescuentoBloque2,
      porcentajeTotalDescuento,
      montoAhorroTotal,
      precioFinal,
      precioTrasBloque1: roundMoney(precioOriginal - montoDescuentoBloque1),
    };
  }

  function getProductInfo(root) {
    return root?.closest?.('product-info') || null;
  }

  function getSelectedVariant(root) {
    const productInfo = getProductInfo(root);
    if (!productInfo) return null;

    const selectedVariantNode = productInfo.querySelector(
      'product-variant-selects [data-selected-variant]'
    );
    if (!selectedVariantNode?.textContent) return null;

    try {
      const variant = JSON.parse(selectedVariantNode.textContent);
      return variant && typeof variant === 'object' ? variant : null;
    } catch (error) {
      return null;
    }
  }

  function getSkuFromProductInfo(root) {
    const variant = getSelectedVariant(root);
    return variant?.sku ? String(variant.sku) : '';
  }

  function getPriceFromProductInfo(root) {
    const variant = getSelectedVariant(root);
    const price = Number(variant?.price);
    return Number.isFinite(price) && price > 0 ? price : 0;
  }

  function isSaleTrue(value) {
    return value === true || value === 1 || String(value).toLowerCase() === 'true';
  }

  function isPromoEligible(precio) {
    if (!precio || typeof precio !== 'object') return false;

    const descuentos = Array.isArray(precio.descuentos) ? precio.descuentos : [];
    const descuentoOrden4 = descuentos.find((item) => Number(item?.orden) === 4);
    if (!descuentoOrden4) return false;

    const fraccion = Number(descuentoOrden4.fraccion);
    return Number.isFinite(fraccion) && fraccion > 0 && isSaleTrue(descuentoOrden4.sale);
  }

  function buildSaleBadgeLabel(breakdown) {
    if (!breakdown) return '';

    const parts = [];
    const bloque1Label = formatPercentLabel(breakdown.porcentajeBloque1);
    const bloque2Label = formatPercentLabel(breakdown.porcentajeBloque2);

    if (Number(bloque1Label) > 0) {
      parts.push(`-${bloque1Label}%`);
    }
    if (Number(bloque2Label) > 0) {
      parts.push(`-${bloque2Label}%`);
    }

    return parts.join(' ');
  }

  function buildGalleryDiscountLabel(breakdown) {
    if (!breakdown) return '';

    const parts = [];
    const bloque1Label = formatPercentLabel(breakdown.porcentajeBloque1);
    const bloque2Label = formatPercentLabel(breakdown.porcentajeBloque2);

    if (Number(bloque1Label) > 0) {
      parts.push(`${bloque1Label}%`);
    }
    if (Number(bloque2Label) > 0) {
      parts.push(`${bloque2Label}%`);
    }

    if (!parts.length) return '';
    return `${parts.join(' + ')} OFF`;
  }

  function getCachedPrecio(sku) {
    const entry = precioCache.get(sku);
    if (!entry) return null;
    if (Date.now() - entry.cachedAt > CACHE_TTL_MS) {
      precioCache.delete(sku);
      return null;
    }
    return entry.precio;
  }

  function setCachedPrecio(sku, precio) {
    if (precioCache.size >= 30) {
      const oldestKey = precioCache.keys().next().value;
      if (oldestKey != null) precioCache.delete(oldestKey);
    }
    precioCache.set(sku, { precio, cachedAt: Date.now() });
  }

  function getModalMciOpener(root) {
    const productInfo = getProductInfo(root);
    if (!productInfo) return null;
    return productInfo.querySelector('[data-modal-mci-opener]');
  }

  function setModalMciOpenerVisible(root, shouldShow) {
    const opener = getModalMciOpener(root);
    if (!opener) return;

    if (shouldShow) {
      opener.hidden = false;
      opener.removeAttribute('hidden');
      opener.style.removeProperty('display');
    } else {
      opener.hidden = true;
      opener.setAttribute('hidden', '');
      opener.style.display = 'none';
    }
  }

  function getSkuUltimasPiezas(root) {
    const productInfo = getProductInfo(root);
    if (!productInfo) return null;
    return productInfo.querySelector('[data-sku-ultimas-piezas]');
  }

  function setSkuUltimasPiezasVisible(root, shouldShow) {
    const badge = getSkuUltimasPiezas(root);
    if (!badge) return;

    if (shouldShow) {
      badge.hidden = false;
      badge.removeAttribute('hidden');
    } else {
      badge.hidden = true;
      badge.setAttribute('hidden', '');
    }
  }

  function getSectionScope(root) {
    return root?.closest?.('.shopify-section') || document;
  }

  function setPromoMediaBadgesVisible(root, shouldShow, label = '') {
    const badges = getSectionScope(root).querySelectorAll(
      '[data-pdp-promo-media-badge]'
    );

    badges.forEach((badge) => {
      const labelEl = badge.querySelector('[data-pdp-promo-media-badge-label]');
      if (labelEl && label) {
        labelEl.textContent = label;
      }

      if (shouldShow && label) {
        badge.hidden = false;
        badge.removeAttribute('hidden');
      } else {
        badge.hidden = true;
        badge.setAttribute('hidden', '');
        if (labelEl && !shouldShow) {
          labelEl.textContent = '';
        }
      }
    });
  }

  class PromocionDescuentos extends HTMLElement {
    connectedCallback() {
      if (this._promocionBound) return;
      this._promocionBound = true;

      this.uiEl = this.querySelector('[data-promocion-ui]');
      if (!this.uiEl) {
        this.uiEl = document.createElement('div');
        this.uiEl.className = 'promocion-descuentos__ui';
        this.uiEl.setAttribute('data-promocion-ui', '');
        this.appendChild(this.uiEl);
      }

      this.moneyFormat =
        this.dataset.moneyFormat ||
        window?.theme?.settings?.money_format ||
        '${{amount}}';

      this.currentPrice =
        Number(this.dataset.price || 0) || getPriceFromProductInfo(this);
      this.lastPrecio = null;
      this.galleryDiscountLabel = '';
      this.requestId = 0;
      this.subscribeRetries = 0;

      this.bindVariantListener();

      const initialSku = getSkuFromProductInfo(this) || this.dataset.sku || '';
      this.currentSku = initialSku;
      this.dataset.sku = initialSku;
      this.scheduleInitialFetch(initialSku);
    }

    disconnectedCallback() {
      this.teardown();
      this._promocionBound = false;
    }

    teardown() {
      if (this.idleCallbackId != null && typeof cancelIdleCallback === 'function') {
        cancelIdleCallback(this.idleCallbackId);
        this.idleCallbackId = null;
      }
      if (this.idleTimeoutId != null) {
        clearTimeout(this.idleTimeoutId);
        this.idleTimeoutId = null;
      }
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      if (typeof this.unsubscribe === 'function') {
        this.unsubscribe();
        this.unsubscribe = null;
      }
    }

    scheduleInitialFetch(sku) {
      const run = () => {
        this.idleCallbackId = null;
        this.idleTimeoutId = null;
        if (!this.isConnected) return;
        this.fetchForSku(sku);
      };

      // Evita competir con LCP/recursos críticos en el primer paint.
      if (typeof requestIdleCallback === 'function') {
        this.idleCallbackId = requestIdleCallback(run, { timeout: 1200 });
      } else {
        this.idleTimeoutId = window.setTimeout(run, 200);
      }
    }

    setVisible(isVisible) {
      if (isVisible) {
        this.hidden = false;
        this.removeAttribute('hidden');
        this.style.removeProperty('display');
        this.classList.add('promocion-descuentos--visible');
        this.dataset.promoState = 'visible';
        setModalMciOpenerVisible(this, false);
        setSkuUltimasPiezasVisible(this, true);
        setPromoMediaBadgesVisible(this, true, this.galleryDiscountLabel);
      } else {
        this.hidden = true;
        this.setAttribute('hidden', '');
        this.classList.remove('promocion-descuentos--visible');
        this.dataset.promoState = 'hidden';
        setModalMciOpenerVisible(this, true);
        setSkuUltimasPiezasVisible(this, false);
        setPromoMediaBadgesVisible(this, false);
        this.galleryDiscountLabel = '';
      }
    }

    clearContent() {
      if (this.uiEl) this.uiEl.replaceChildren();
    }

    hideBlock() {
      this.lastPrecio = null;
      this.lastBreakdown = null;
      this.clearContent();
      this.setVisible(false);
    }

    applyPrecio(precio) {
      if (!isPromoEligible(precio)) {
        this.dataset.promoState = 'ineligible';
        this.hideBlock();
        return;
      }

      const shopifyPriceCents =
        Number(this.currentPrice || this.dataset.price || 0) ||
        getPriceFromProductInfo(this);
      const apiListPrice = Number(
        precio?.listaTachado ?? precio?.precioLista ?? precio?.precioOriginal
      );
      const precioBase =
        Number.isFinite(apiListPrice) && apiListPrice > 0
          ? apiListPrice
          : shopifyPriceCents / 100;

      const breakdown = calcularDescuentosAgrupados(
        precioBase,
        precio?.descuentos
      );

      if (!breakdown || !(breakdown.porcentajeTotalDescuento > 0)) {
        this.dataset.promoState = 'ineligible';
        this.hideBlock();
        return;
      }

      this.galleryDiscountLabel = buildGalleryDiscountLabel(breakdown);
      this.lastPrecio = precio;
      this.lastBreakdown = breakdown;
      this.dataset.promoState = 'eligible';
      this.render(breakdown);
      this.setVisible(true);
    }

    bindVariantListener() {
      if (typeof this.unsubscribe === 'function') {
        this.unsubscribe();
        this.unsubscribe = null;
      }

      const attach = () => {
        if (
          typeof subscribe !== 'function' ||
          typeof PUB_SUB_EVENTS === 'undefined'
        ) {
          return false;
        }

        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (!this.isConnected) return;

          const variant = event?.data?.variant;
          if (!variant) return;

          const nextSku = variant.sku
            ? String(variant.sku)
            : getSkuFromProductInfo(this) || '';
          const nextPrice = Number(variant.price);

          if (Number.isFinite(nextPrice) && nextPrice > 0) {
            this.currentPrice = nextPrice;
            this.dataset.price = String(nextPrice);
          }

          if (nextSku && nextSku !== this.currentSku) {
            this.currentSku = nextSku;
            this.dataset.sku = nextSku;
            this.fetchForSku(nextSku);
            return;
          }

          if (this.lastPrecio && isPromoEligible(this.lastPrecio)) {
            const shopifyPriceCents =
              Number(this.currentPrice || this.dataset.price || 0) ||
              getPriceFromProductInfo(this);
            const apiListPrice = Number(
              this.lastPrecio?.listaTachado ??
                this.lastPrecio?.precioLista ??
                this.lastPrecio?.precioOriginal
            );
            const precioBase =
              Number.isFinite(apiListPrice) && apiListPrice > 0
                ? apiListPrice
                : shopifyPriceCents / 100;
            const breakdown = calcularDescuentosAgrupados(
              precioBase,
              this.lastPrecio?.descuentos
            );
            if (breakdown && breakdown.porcentajeTotalDescuento > 0) {
              this.galleryDiscountLabel = buildGalleryDiscountLabel(breakdown);
              this.lastBreakdown = breakdown;
              this.render(breakdown);
              this.setVisible(true);
            } else {
              this.hideBlock();
            }
          }
        });

        return true;
      };

      if (attach()) return;

      if (this.subscribeRetries >= 20) return;
      this.subscribeRetries += 1;

      window.setTimeout(() => {
        if (!this.isConnected) return;
        this.bindVariantListener();
      }, 50);
    }

    async fetchForSku(sku) {
      if (!sku) {
        this.hideBlock();
        return;
      }

      const cached = getCachedPrecio(sku);
      if (cached) {
        this.applyPrecio(cached);
        return;
      }

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      const currentRequestId = ++this.requestId;
      const apiKey = this.dataset.ksync || '';

      // No ocultamos durante loading: evita flicker MCI + CLS al cambiar variante.
      this.dataset.promoState = 'loading';
      this.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(
          `${API_BASE}?sku=${encodeURIComponent(sku)}`,
          {
            method: 'GET',
            headers: { KSync: apiKey },
            credentials: 'omit',
            mode: 'cors',
            redirect: 'error',
            priority: 'low',
            signal,
          }
        );

        if (!response.ok) throw new Error(`Error ${response.status}`);

        const payload = await response.json();
        if (signal.aborted || currentRequestId !== this.requestId) return;

        const precio = payload?.precio;
        if (!precio || typeof precio !== 'object') {
          throw new Error('Respuesta sin datos de precio');
        }

        setCachedPrecio(sku, precio);
        this.applyPrecio(precio);
      } catch (error) {
        if (error?.name === 'AbortError' || currentRequestId !== this.requestId) {
          return;
        }
        console.error('[promocion-descuentos]', error);
        this.dataset.promoState = 'error';
        this.hideBlock();
      } finally {
        if (currentRequestId === this.requestId) {
          this.removeAttribute('aria-busy');
        }
      }
    }

    render(breakdown) {
      if (!this.uiEl) return;

      const calc =
        breakdown && typeof breakdown.precioFinal === 'number'
          ? breakdown
          : null;

      if (!calc || !(calc.porcentajeTotalDescuento > 0)) {
        this.hideBlock();
        return;
      }

      const shopifyPriceCents =
        Number(this.currentPrice || this.dataset.price || 0) ||
        getPriceFromProductInfo(this);

      if (!Number.isFinite(shopifyPriceCents) || shopifyPriceCents <= 0) {
        this.hideBlock();
        return;
      }

      const saleBadgeLabel = buildSaleBadgeLabel(calc);
      const cashPriceCents = Math.floor(
        shopifyPriceCents * (1 - CASH_EXTRA_DISCOUNT)
      );
      const shopifyPriceLabel = formatMoney(shopifyPriceCents, this.moneyFormat);
      const cashPriceLabel = formatMoney(cashPriceCents, this.moneyFormat);

      let rowsHtml = '';

      if (saleBadgeLabel) {
        rowsHtml += `
          <span class="promocion-descuentos__badge promocion-descuentos__badge--sale product__price-discount">${escapeHtml(saleBadgeLabel)}</span>
          <span class="promocion-descuentos__amount promocion-descuentos__amount--meses">${escapeHtml(shopifyPriceLabel)}</span>
          <span class="promocion-descuentos__label promocion-descuentos__label--meses">Precio a meses</span>
        `;
      }

      rowsHtml += `
        <span class="promocion-descuentos__badge product__price-discount">-10% extra</span>
        <span class="promocion-descuentos__amount promocion-descuentos__amount--contado">${escapeHtml(cashPriceLabel)}</span>
        <span class="promocion-descuentos__label promocion-descuentos__label--contado">Precio de contado</span>
      `;

      this.uiEl.innerHTML = rowsHtml;
    }
  }

  if (!customElements.get('promocion-descuentos')) {
    customElements.define('promocion-descuentos', PromocionDescuentos);
  }

  // Expone el helper para reutilizar / probar fuera del custom element.
  window.GaiaDescuentos = Object.assign(window.GaiaDescuentos || {}, {
    calcularDescuentosAgrupados,
    normalizeDiscountPercents,
    roundMoney,
    roundPercent,
  });
})();
